/**
 * The strip a form shows when useFormDraft put unsaved values back.
 *
 * Restoring silently would be worse than not restoring at all: the user opens
 * what they expect to be a blank form, finds it populated, and cannot tell
 * whether those values are theirs, a leftover, or something the system chose.
 * This says where the values came from and gives one click to clear them.
 *
 * The action reads "Clear" rather than "Discard" — it empties the form in front
 * of the user, which is what they are choosing, not filing something away.
 */

import React from 'react';

interface DraftRestoredBannerProps {
  /** Only renders when true — the form can pass draft.restored straight through. */
  show: boolean;
  /** When the draft was last written, for the "saved …" hint. */
  savedAt?: Date | null;
  /** Clears the draft and empties the form. */
  onClear: () => void;
  className?: string;
}

function timeAgo(at: Date): string {
  const secs = Math.max(0, Math.floor((Date.now() - at.getTime()) / 1000));
  if (secs < 60) return 'moments ago';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

const DraftRestoredBanner: React.FC<DraftRestoredBannerProps> = ({
  show,
  savedAt,
  onClear,
  className = '',
}) => {
  if (!show) return null;

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/30 ${className}`}
      role="status"
    >
      <i className="ri-draft-line text-amber-600 dark:text-amber-400" />
      <span className="text-xs text-amber-900 dark:text-amber-100">
        Restored what you had entered last time
        {savedAt ? <span className="text-amber-700 dark:text-amber-300"> · saved {timeAgo(savedAt)}</span> : null}
      </span>
      <button
        type="button"
        onClick={onClear}
        className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100"
        title="Clear these values and start a blank form"
      >
        <i className="ri-close-line" /> Clear
      </button>
    </div>
  );
};

export default DraftRestoredBanner;
